import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { AxiosResponse } from "axios";
import { useEffect, useState } from "react";

import request from "@/api";
import type { CustomAxiosRequestConfig } from "@/api";
import {
    DateRangePicker as ZhDateRangePicker,
    Table as ZhTable,
    type TableColumnProps,
    Button as ZhFilterButton,
    Input as ZhFilterInput
} from "zhihao-ui-react";
import { dateTimeFormat } from "@/utils";
import { fmtMoney } from "@/utils/money";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { useUserStore } from "@/views/stores";
import { ZoneRechargePage, type ZoneRechargePageResult, type ZoneRechargeQuery } from "./ZoneRechargePage";
import { ZoneConsumptionPage, type ZoneConsumptionPageResult, type ZoneConsumptionQuery } from "./ZoneConsumptionPage";

export interface ZoneAccountRow {
    account?: { id?: string | number; createdTime?: string };
    specialZoneName?: string;
    realName?: string;
    customerCode?: string;
    mobile?: string;
    institutionName?: string;
    balanceAmount?: number;
}

export interface ZoneAccountPageResult {
    list?: ZoneAccountRow[];
    total?: number;
}

export interface ZoneAccountQuery {
    page: number;
    size: number;
    accountId?: string;
    keyword?: string;
    startDate?: string;
    endDate?: string;
    sortKey?: string;
    sortOrder?: "asc" | "desc";
}

export interface ZoneAccountPageProps {
    loadAccounts?: (query: ZoneAccountQuery) => Promise<ZoneAccountPageResult>;
    loadRecharges?: (query: ZoneRechargeQuery) => Promise<ZoneRechargePageResult>;
    loadConsumptions?: (query: ZoneConsumptionQuery) => Promise<ZoneConsumptionPageResult>;
    permissions?: string[];
}

const defaultQuery: ZoneAccountQuery = { page: 1, size: 10 };

/** 复用 Vue 专区余额管理账户查询接口。 */
export async function loadZoneAccounts(query: ZoneAccountQuery): Promise<ZoneAccountPageResult> {
    const response = await request.service.get<ZoneAccountPageResult>("/admin/special-zone-account/paging", {
        params: query
    });
    return response.data;
}

/** 复用 Vue 专区余额管理账户导出接口。 */
export async function exportZoneAccounts(query: ZoneAccountQuery): Promise<void> {
    const response = await request.service.get<Blob, AxiosResponse<Blob>>("/admin/special-zone-account/export", {
        params: query,
        responseType: "blob",
        returnRawResponse: true
    } as CustomAxiosRequestConfig);
    const contentDisposition = response.headers["content-disposition"];
    const filename =
        contentDisposition?.match(/filename\*=(?:UTF-8'')?([^;]+)/i)?.[1]?.trim() ||
        contentDisposition?.match(/filename=([^;]+)/i)?.[1]?.trim() ||
        "专区账户列表.xlsx";
    const objectUrl = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.setAttribute("download", filename.replace(/^['"]|['"]$/g, ""));
    document.body.appendChild(link);
    try {
        link.click();
    } finally {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
    }
}

/** 将 Vue 账户列表中的主体信息转换为原页面显示文案。 */
function getCustomerLabel(row: ZoneAccountRow): string {
    return [row.realName, row.customerCode].filter(Boolean).join(" | ") || "--";
}

/** 专区余额管理首屏，保留 Vue 三 Tab 和账户查询列表契约。 */
export function ZoneAccountPage({
    loadAccounts = loadZoneAccounts,
    loadRecharges,
    loadConsumptions,
    permissions
}: ZoneAccountPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const currentPermissions = permissions ?? storePermissions;
    const canManage = currentPermissions.includes("ROLE_ZONE_ACCOUNT_MANAGE");
    const [activeTab, setActiveTab] = useState<"account" | "recharge" | "consumption">("account");
    const [rows, setRows] = useState<ZoneAccountRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<ZoneAccountQuery>(defaultQuery);
    const [accountId, setAccountId] = useState("");
    const [keyword, setKeyword] = useState("");
    const [createdDateRange, setCreatedDateRange] = useState<[string | null, string | null]>([null, null]);
    const [rechargeAccountId, setRechargeAccountId] = useState<string>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        let disposed = false;
        void loadAccounts(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("专区账户加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadAccounts, query]);

    /** 保留 Vue 列表筛选参数名称和重置到第一页的行为。 */
    const search = () => {
        setQuery({
            ...defaultQuery,
            ...(accountId.trim() ? { accountId: accountId.trim() } : {}),
            ...(keyword.trim() ? { keyword: keyword.trim() } : {}),
            ...(createdDateRange[0] ? { startDate: createdDateRange[0] } : {}),
            ...(createdDateRange[1] ? { endDate: createdDateRange[1] } : {})
        });
    };

    /** 保留 Vue 开户时间的后端排序字段和升降序参数。 */
    const handleSortChange = ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
        if (prop !== "account.createdTime") return;
        setQuery(current => {
            const nextQuery: ZoneAccountQuery = { ...current, page: 1 };
            delete nextQuery.sortKey;
            delete nextQuery.sortOrder;
            if (order) {
                nextQuery.sortKey = "created_time";
                nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
            }
            return nextQuery;
        });
    };

    const tab = (value: "account" | "recharge" | "consumption", label: string) => (
        <button
            aria-selected={activeTab === value}
            className="zone-account-tab cursor-pointer border-0 border-b-2 border-transparent bg-transparent px-0 pb-2 text-gray-500"
            role="tab"
            type="button"
            onClick={() => {
                // 对齐 Vue changeTab：离开充值明细后清空账户带入的筛选条件。
                if (value !== "recharge") setRechargeAccountId(undefined);
                setActiveTab(value);
            }}
        >
            {label}
        </button>
    );

    return (
        <section aria-label="zone-account-page" className="flex h-full min-h-full flex-col">
            <Card>
                <div aria-label="专区余额管理页签" className="zone-account-tabs mb-4 flex border-b" role="tablist">
                    {tab("account", "账户查询")}
                    {tab("recharge", "充值明细")}
                    {tab("consumption", "消费明细")}
                </div>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                {activeTab === "account" ? (
                    <>
                        <ZhTable
                            columns={normalizeOperationColumns(
                                [
                                    {
                                        label: "账户编号",
                                        minWidth: 176,
                                        prop: "accountId",
                                        render: ({ row }) => row.account?.id || "--"
                                    },
                                    {
                                        label: "所属专区",
                                        minWidth: 176,
                                        prop: "zone",
                                        render: ({ row }) => row.specialZoneName || "--"
                                    },
                                    {
                                        label: "个人实名/用户ID",
                                        minWidth: 204,
                                        prop: "customer",
                                        render: ({ row }) => getCustomerLabel(row)
                                    },
                                    {
                                        label: "手机号",
                                        width: 176,
                                        prop: "mobile",
                                        render: ({ row }) => row.mobile || "--"
                                    },
                                    {
                                        label: "机构名称",
                                        minWidth: 176,
                                        prop: "institution",
                                        render: ({ row }) => row.institutionName || "--"
                                    },
                                    {
                                        label: "专区余额（元)",
                                        width: 176,
                                        prop: "balance",
                                        render: ({ row }) => fmtMoney(row.balanceAmount)
                                    },
                                    {
                                        label: "开户时间",
                                        minWidth: 154,
                                        prop: "account.createdTime",
                                        sortable: true,
                                        render: ({ row }) =>
                                            dateTimeFormat(row.account?.createdTime, "YYYY-MM-DD HH:mm")
                                    },
                                    {
                                        label: "操作",
                                        fixed: "right",
                                        width: 88,
                                        className: "admin-table-operation-column compact",
                                        labelClassName: "admin-table-operation-column compact",
                                        prop: "operation",
                                        render: ({ row }) =>
                                            canManage ? (
                                                <Button
                                                    aria-label="充值明细"
                                                    size="small"
                                                    onClick={() => {
                                                        setRechargeAccountId(row.customerCode);
                                                        setActiveTab("recharge");
                                                    }}
                                                >
                                                    充值明细
                                                </Button>
                                            ) : null
                                    }
                                ] as TableColumnProps<ZoneAccountRow>[],
                                rows
                            )}
                            data={rows}
                            pagination={false}
                            onSortChange={handleSortChange}
                            rowKey={row => String(row.account?.id || row.customerCode)}
                            isShowSearchBtn={false}
                            tableHeader={
                                <div className="admin-table-composed-header">
                                    <Space className="admin-table-filter-form mb-4" wrap>
                                        <ZhFilterInput
                                            aria-label="账户编号"
                                            allowClear
                                            className="zone-account-id"
                                            placeholder="请输入账户编号搜索"
                                            value={accountId}
                                            onChange={event => setAccountId(event.target.value)}
                                        />
                                        <ZhFilterInput
                                            aria-label="用户关键词"
                                            className="zone-account-keyword"
                                            allowClear
                                            placeholder="请输入用户ID/个人或机构实名/手机号搜索"
                                            value={keyword}
                                            onChange={event => setKeyword(event.target.value)}
                                        />
                                        <label className="zone-account-date">
                                            开户日期
                                            <ZhDateRangePicker
                                                type="daterange"
                                                format="YYYY-MM-DD"
                                                valueFormat="YYYY-MM-DD"
                                                rangeSeparator="-"
                                                startPlaceholder="开户开始日期"
                                                endPlaceholder="开户结束日期"
                                                value={createdDateRange}
                                                onChange={(_value, formattedValue) =>
                                                    setCreatedDateRange(
                                                        formattedValue
                                                            ? [formattedValue[0] || null, formattedValue[1] || null]
                                                            : [null, null]
                                                    )
                                                }
                                            />
                                        </label>
                                        <ZhFilterButton
                                            aria-label="查询账户"
                                            icon={<TableSearchIcon name="search" />}
                                            type="primary"
                                            onClick={search}
                                        >
                                            搜索
                                        </ZhFilterButton>
                                    </Space>
                                </div>
                            }
                            append={
                                <div className="admin-table-composed-pagination">
                                    <ZhPagination
                                        className="mt-4 self-end"
                                        current={query.page}
                                        pageSize={query.size}
                                        total={total}
                                        onChange={(page, size) => setQuery(current => ({ ...current, page, size }))}
                                    />
                                </div>
                            }
                            onReset={() => {
                                // 对齐 Vue ProTable：重置筛选条件后回到账户列表第一页。
                                setAccountId("");
                                setKeyword("");
                                setCreatedDateRange([null, null]);
                                setQuery(defaultQuery);
                            }}
                        />
                    </>
                ) : activeTab === "recharge" ? (
                    <ZoneRechargePage
                        accountId={rechargeAccountId}
                        loadRecharges={loadRecharges}
                        permissions={currentPermissions}
                    />
                ) : (
                    <ZoneConsumptionPage loadConsumptions={loadConsumptions} permissions={currentPermissions} />
                )}
            </Card>
        </section>
    );
}

export default ZoneAccountPage;
